import React, { useState, useEffect, useRef } from 'react';
import { X, Wallet, CreditCard, FileText, LogOut, User, Mail, Phone, TrendingUp, Plus } from 'lucide-react';

export default function PartnerSidebar({ 
  isOpen, 
  onClose, 
  userData, 
  onLogout, 
  onMarginChange,
  onRechargeClick,
  onTransactionHistoryClick 
}) {
  const [showMarginModal, setShowMarginModal] = useState(false);
  const [tempMargin, setTempMargin] = useState(userData.margin);
  const [isUpdating, setIsUpdating] = useState(false);
  const sidebarRef = useRef(null);

  useEffect(() => {
    setTempMargin(userData.margin);
  }, [userData.margin]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target) && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleMarginUpdate = async () => {
    try {
      setIsUpdating(true);
      
      const token = localStorage.getItem('authToken');
      
      const response = await fetch('https://djebooking.com/api/user/margin', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ margin: tempMargin })
      });

      if (!response.ok) throw new Error('Failed to update margin');

      const storedUser = localStorage.getItem('userData');
      if (storedUser) {
        const userDataParsed = JSON.parse(storedUser);
        userDataParsed.margin = tempMargin;
        localStorage.setItem('userData', JSON.stringify(userDataParsed));
      }

      if (onMarginChange) {
        onMarginChange(tempMargin);
      }

      setShowMarginModal(false);

    } catch (error) {
      setTempMargin(userData.margin);
    } finally {
      setIsUpdating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
        style={{ opacity: isOpen ? 1 : 0 }}
      />

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className="fixed top-0 right-0 h-full w-96 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto"
        style={{ transform: isOpen ? 'translateX(0)' : 'translateX(100%)' }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#2B4979] to-[#3d5c8f] text-white p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Mon Compte</h2>
            <button
              onClick={onClose}
              className="hover:bg-white/20 p-2 rounded-full transition"
            >
              <X size={24} />
            </button>
          </div>

          {/* User Info */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <User size={16} />
              <span className="text-sm">{userData.prenom} {userData.nom}</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail size={16} />
              <span className="text-sm">{userData.email}</span>
            </div>
            {userData.tel && (
              <div className="flex items-center gap-2">
                <Phone size={16} />
                <span className="text-sm">{userData.tel}</span>
              </div>
            )}
          </div>
        </div>

        {/* Balance Section */}
        <div className="p-6 bg-gradient-to-br from-green-50 to-green-100 border-b">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-green-700">
              <CreditCard size={20} />
              <span className="font-semibold">Solde actuel</span>
            </div>
          </div>
          <div className="text-3xl font-bold text-green-600 mb-4">
            {userData.balance.toFixed(3)} TND
          </div>
          <button
            onClick={() => {
              onClose();
              if (onRechargeClick) onRechargeClick();
            }}
            className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 rounded-lg transition flex items-center justify-center gap-2"
          >
            <Plus size={18} />
            Recharger mon solde
          </button>
        </div>

        {/* Margin Section */}
        <div className="p-6 border-b">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-[#FF7B47]">
              <TrendingUp size={20} />
              <span className="font-semibold">Ma Marge</span>
            </div>
            <span className="text-2xl font-bold text-[#FF7B47]">{userData.margin}%</span>
          </div>
          
          <button
            onClick={() => setShowMarginModal(true)}
            className="w-full bg-[#FF7B47] hover:bg-[#ff6a30] text-white font-semibold py-3 rounded-lg transition"
          >
            Modifier la marge
          </button>

          {/* Margin Modal */}
          {showMarginModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-lg text-[#2B4979]">Ajuster votre marge</h3>
                  <button
                    onClick={() => setShowMarginModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Marge actuelle</span>
                    <span className="text-2xl font-bold text-[#FF7B47]">{tempMargin}%</span>
                  </div>
                  
                  <div className="relative w-full h-3 bg-gray-200 rounded-full overflow-hidden mb-4">
                    <div 
                      className="absolute top-0 left-0 h-full bg-gradient-to-r from-[#FF7B47] to-[#ff6a30] transition-all duration-300 rounded-full"
                      style={{ width: `${tempMargin}%` }}
                    ></div>
                  </div>

                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={tempMargin}
                    onChange={(e) => setTempMargin(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #FF7B47 0%, #FF7B47 ${tempMargin}%, #e5e7eb ${tempMargin}%, #e5e7eb 100%)`
                    }}
                  />

                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>0%</span>
                    <span>50%</span>
                    <span>100%</span>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-2 mb-4">
                  {[10, 15, 20, 25].map((preset) => (
                    <button
                      key={preset}
                      onClick={() => setTempMargin(preset)}
                      className={`py-2 px-3 rounded-lg text-sm font-semibold transition ${
                        tempMargin === preset
                          ? 'bg-[#FF7B47] text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {preset}%
                    </button>
                  ))}
                </div>

                <button
                  onClick={handleMarginUpdate}
                  disabled={isUpdating}
                  className={`w-full py-3 rounded-lg font-semibold transition ${
                    isUpdating
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-[#2B4979] hover:bg-[#1e3454] text-white'
                  }`}
                >
                  {isUpdating ? 'Mise à jour...' : 'Appliquer la marge'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Transaction History Button */}
        <div className="p-6 border-b">
          <button
            onClick={() => {
              onClose();
              if (onTransactionHistoryClick) onTransactionHistoryClick();
            }}
            className="w-full bg-[#2B4979] hover:bg-[#1e3454] text-white font-semibold py-3 rounded-lg transition flex items-center justify-center gap-2"
          >
            <FileText size={18} />
            Historique des transactions
          </button>
        </div>

        {/* Logout Button */}
        <div className="p-6">
          <button
            onClick={() => {
              onClose();
              if (onLogout) onLogout();
            }}
            className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold py-3 rounded-lg transition flex items-center justify-center gap-2"
          >
            <LogOut size={18} />
            Déconnexion
          </button>
        </div>
      </div>

      <style jsx>{`
        input[type="range"]::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #FF7B47;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        
        input[type="range"]::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #FF7B47;
          cursor: pointer;
          border: none;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
      `}</style>
    </>
  );
}
